import React from 'react';
import { useTranslation } from 'react-i18next';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import Modal from '../../shared/components/UI/Modal';
import * as API from '../../api';

const RatingModal = ({ shown, close, order }) => {
  const { t } = useTranslation();
  const [ratingDone, setRatingDone] = React.useState(false);
  const [reviewExist, setReviewExist] = React.useState({});
  const [rating, setRating] = React.useState(0);
  const [hoverRating, setHoverRating] = React.useState(0);
  const [loadingRating, setLoadingRating] = React.useState(false);
  const [reviewHelper, setReviewHelper] = React.useState({ type: '', message: '' });
  const [ratingHelper, setRatingHelper] = React.useState('');

  const submitRating = (event) => {
    event.preventDefault();
    setRatingHelper('');
    const formData = new FormData(document.getElementById('form-review-order'));
    const _form = {
      reservation_id: order.id,
      rating: rating,
      title: formData.get('title'),
      comment: formData.get('body'),
    };
    if (!rating || rating === 0) {
      setRatingHelper(t('account.ratingRequired'));
      return;
    }
    setLoadingRating(true);
    API.SendReview(_form)
      .then((res) => {
        setReviewHelper({ type: 'success', message: res.message || t('account.ratingThanks') });
        setRatingDone(true);
        setReviewExist(_form);
      })
      .catch((err) => {
        setReviewHelper({ type: 'error', message: err.data.errors });
      })
      .then(() => {
        setLoadingRating(false);
        window.location.reload();
      });
  };

  React.useEffect(() => {
    const Auth = API.AuthStorage;
    if (Object.keys(order).length) {
      (order.reviews || []).forEach((review) => {
        if (review.user_id == Auth.user.id) setReviewExist(review);
      });
    } else {
      setReviewExist({});
    }
  }, [order]);

  const display = hoverRating || rating;
  const alreadyReviewed = Object.keys(reviewExist).length;

  return (
    <Modal
      shown={shown}
      close={close}
      size="modal--medium"
      title={t('account.ratingTitle')}
      subtitle={!alreadyReviewed ? t('account.ratingSubtitle') : undefined}
    >
      {reviewHelper.message ? (
        <div
          className={`mb-4 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm ${
            reviewHelper.type === 'success'
              ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
              : 'border-rose-100 bg-rose-50 text-rose-600'
          }`}
        >
          {reviewHelper.type === 'success' ? (
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div dangerouslySetInnerHTML={{ __html: reviewHelper.message }} />
        </div>
      ) : null}

      {alreadyReviewed ? (
        // Read-only — user already reviewed
        <div className="space-y-3 text-center">
          <Stars value={reviewExist.rating} readOnly />
          {reviewExist.title ? <h3 className="font-display text-lg font-semibold text-ink">{reviewExist.title}</h3> : null}
          {reviewExist.comment ? <p className="text-sm text-ink-muted">{reviewExist.comment}</p> : null}
        </div>
      ) : (
        <form onSubmit={submitRating} method="POST" id="form-review-order" className="space-y-4">
          <div className="flex flex-col items-center gap-1">
            <Stars
              value={display}
              onSet={(v) => setRating(v)}
              onHover={(v) => setHoverRating(v)}
              onLeave={() => setHoverRating(0)}
              disabled={loadingRating || ratingDone}
            />
            {ratingHelper ? <p className="text-xs text-rose-500">{ratingHelper}</p> : null}
          </div>

          <input
            name="title"
            disabled={loadingRating || ratingDone}
            placeholder={t('account.ratingTitlePlaceholder')}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <textarea
            name="body"
            rows={3}
            disabled={loadingRating || ratingDone}
            placeholder={t('account.ratingCommentPlaceholder')}
            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />

          <button
            type="submit"
            disabled={loadingRating || ratingDone}
            className="flex w-full items-center justify-center rounded-xl bg-accent-500 px-4 py-3 text-sm font-bold text-brand-900 shadow-card transition-colors hover:bg-accent-400 disabled:opacity-60"
          >
            {loadingRating ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-900/30 border-t-brand-900" />
            ) : (
              t('account.ratingPublish')
            )}
          </button>
        </form>
      )}
    </Modal>
  );
};

function Stars({ value = 0, onSet, onHover, onLeave, readOnly, disabled }) {
  return (
    <div className="flex items-center gap-1" onMouseLeave={onLeave}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= value;
        const Icon = active ? StarSolid : StarOutline;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly || disabled}
            onMouseEnter={onHover ? () => onHover(n) : undefined}
            onClick={onSet ? () => onSet(n) : undefined}
            className={readOnly ? 'cursor-default' : 'transition-transform hover:scale-110'}
            aria-label={`${n} stars`}
          >
            <Icon className={`h-8 w-8 ${active ? 'text-accent-500' : 'text-slate-300'}`} />
          </button>
        );
      })}
    </div>
  );
}

export default RatingModal;
